<template>
  <div>
    <h4 class="mb-2">
      Mindestlohn-Konfigurationen
    </h4>

    <v-card
      v-if="isSupplementaryContract"
      class="p-4"
    >
      <EditionSupplementaryContract
        :contract-number="contractId"
        :version-id="versionId"
        :edition-id="editionId"
        :can-user-edit="canUserEdit"
      />
    </v-card>

    <u-error-handling
      v-if="errorObject"
      class="mt-4"
      :error-object="errorObject"
    />
    <u-loader v-if="isLoading" />
    <template v-if="!isLoading">
      <div
        v-for="(config, configIndex) in configurations"
        :key="configIndex"
        class="my-4"
      >
        <v-card class="p-3">
          <b-row>
            <b-col class="col-2">
              <router-link
                :to="{
                  name: 'eingabemaske',
                  params: {
                    vertragsId: contractId,
                    versionsId: versionId,
                    editionsId: config.id,
                  },
                }"
              >
                zur Mindestlohnkonfiguration
              </router-link>
            </b-col>
            <b-col class="col-1">
              <div class="row">
                <p class="defaultLabel">
                  Id
                </p>
              </div>
              <div class="row">
                <p class="defaultLabel">
                  Erstellt
                </p>
              </div>
              <div class="row">
                <p class="defaultLabel">
                  Benutzer
                </p>
              </div>
            </b-col>

            <b-col class="col-2">
              <p>{{ config.id }}</p>
              <p>{{ config.created | moment("DD.MM.YYYY HH:mm") }}</p>
              <p>{{ config.createdBy }}</p>
            </b-col>
            <b-col class="col-1">
              <div class="row">
                <p class="defaultLabel">
                  Tenants:
                </p>
              </div>
            </b-col>
            <b-col class="col-2">
              <div
                v-for="(aTenant, aTenantindex) in config.allTenants"
                :key="aTenantindex"
              >
                <b-check
                  :disabled="
                    isTenantCheckboxDisabled(config, configIndex, aTenant)
                  "
                  :checked="isTenantConfigured(config, aTenant.id)"
                  @change="(newValue) => updatedCheckbox(newValue, aTenant.id)"
                >
                  {{ aTenant.bezeichnung }}
                </b-check>
              </div>
            </b-col>
            <b-col
              v-if="showWageSupplement"
              class="col-1"
            >
              <div class="row">
                <p class="defaultLabel">
                  Zuschlagsrechner anzeigen:
                </p>
              </div>
            </b-col>
            <b-col
              v-if="showWageSupplement"
              class="col-"
            >
              <div
                v-for="(aTenant, aTenantindex) in config.allTenants"
                :key="aTenantindex"
              >
                <b-check
                  :disabled="
                    isWageSupplementCheckboxDisabled(
                      config,
                      configIndex,
                      aTenant,
                    )
                  "
                  :checked="isWageSupplementConfigured(config, aTenant.id)"
                  @change="
                    (newValue) =>
                      updatedSupplementCheckbox(newValue, aTenant.id)
                  "
                >
                  {{ aTenant.bezeichnung }}
                </b-check>
              </div>
            </b-col>
            <b-col>
              <button
                v-if="canUserEdit && editConfigIndex != configIndex"
                v-b-tooltip.hover
                type="button"
                placement="bottom"
                title="Konfiguration bearbeiten"
                class="btn btn-primary smallActionButton"
                @click="editConfigIndex = configIndex"
              >
                <i class="fas fa-edit" />
              </button>
              <button
                v-if="canUserEdit && editConfigIndex == configIndex"
                v-b-tooltip.hover
                type="button"
                placement="bottom"
                title="Speichern"
                class="btn btn-success smallActionButton"
                @click="saveConfiguration()"
              >
                <i class="fas fa-check" />
              </button>

              <button
                v-if="canUserEdit && editConfigIndex == configIndex"
                v-b-tooltip.hover
                type="button"
                placement="bottom"
                title="Abbrechen"
                class="btn btn-danger smallActionButton ml-2"
                @click="
                  editConfigIndex = -1;
                  getAllConfigurations();
                "
              >
                <i class="fas fa-times" />
              </button>
              <button
                v-if="canUserEdit && configurations.length > 1"
                v-b-modal.deleteConfiguration
                v-b-tooltip.hover
                type="button"
                placement="bottom"
                title="Konfiguration löschen"
                class="btn btn-danger smallActionButton ml-2"
                @click="currentModalConfig = config.id"
              >
                <i class="fas fa-trash-alt" />
              </button>
              <button
                v-if="canUserEdit"
                v-b-tooltip.hover
                type="button"
                placement="bottom"
                title="Konfiguration kopieren"
                class="btn btn-secondary smallActionButton ml-2"
                @click="copyConfiguration(config.id)"
              >
                <i class="fas fa-copy" />
              </button>
            </b-col>
          </b-row>
        </v-card>

        <hr v-if="configIndex < configurations.length - 1">
      </div>

      <v-btn
        v-if="canUserEdit"
        v-b-tooltip.hover
        class="btn-success mt-4"
        flat
        title="neue Konfiguration hinzufügen"
        @click="addNewConfiguration"
      >
        <i class="fas fa-plus" />
      </v-btn>
      <v-btn
        v-b-tooltip.hover
        class="btn-primary mt-4 ml-2"
        :loading="isLoadingExport"
        flat
        title="Konfigurationen exportieren"
        @click="getConfigurationExport"
      >
        <i class="fas fa-download" />
      </v-btn>
      <v-btn
        v-if="canUserEdit && $IsUserInRole('ImportMiloKonfig')"
        v-b-modal.importConfiguration
        v-b-tooltip.hover
        class="btn-secondary mt-4 ml-2"
        :loading="isLoadingImport"
        flat
        title="Konfigurationen importieren"
      >
        <i class="fas fa-upload" />
      </v-btn>
      <b-modal
        id="deleteConfiguration"
        ref="deleteConfiguration"
        hide-footer
        title="Anlage löschen"
        no-fade
      >
        <div class="d-block mb-4">
          Sind Sie sicher, dass Sie diese Konfiguration löschen möchten?
        </div>
        <b-button
          variant="success"
          @click="deleteConfiguration()"
        >
          Ok
        </b-button>
        <b-button
          class="ml-2"
          variant
          @click="hideModal"
        >
          Abbrechen
        </b-button>
      </b-modal>
      <b-modal
        id="importConfiguration"
        ref="importConfiguration"
        hide-footer
        title="Konfiguration importieren"
        no-fade
      >
        <u-error-handling
          v-if="importErrorObject"
          class="mt-4"
          :error-object="importErrorObject"
        />
        <u-loader v-if="isLoading" />
        <template v-else>
          <div class="d-block mb-4">
            Wählen sie die Datei aus von welcher die Konfigurationen import
            werden sollen.
          </div>
          <div>
            <input
              ref="importMiloSalaryFile"
              class="ml-4 mb-4"
              name="importMiloSalaryFile"
              type="file"
            >
          </div>
          <v-btn
            class="btn-success mt-4 ml-2"
            :loading="isLoadingImport"
            :disabled="isLoadingImport"
            flat
            @click="importResourcecalenderFile"
          >
            Importieren
          </v-btn>
          <v-btn
            class="btn-secondary mt-4 ml-2"
            :loading="isLoadingImport"
            :disabled="isLoadingImport"
            flat
            @click="hideModal"
          >
            Abbrechen
          </v-btn>
        </template>
      </b-modal>
    </template>
  </div>
</template>
<script>
import {
  GetConfigurations,
  SaveConfiguration,
  AddNewConfiguration,
  DeleteConfiguration,
  ExportConfigurations,
  ImportConfigurations,
  CopyConfiguration,
} from "@/services/minimum.wage.services/configuration.minimum.wages.js";
import { GetMainContracts } from "@/services/contract.service.js";
// Fixed import path - since we're already in the contract/edition directory
import EditionSupplementaryContract from "./EditionSupplementaryContract.vue";

export default {
  components: {
    EditionSupplementaryContract,
  },
  props: {
    contractId: {
      type: String,
      default: "",
    },
    versionId: {
      type: Number,
      default: 0,
    },
    editionId: {
      type: Number,
      default: 0,
    },
    canUserEdit: {
      type: Boolean,
      default: false,
    },
    showWageSupplement: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      isLoadingExport: false,
      isLoadingImport: false,
      configurations: [],
      importOverride: false,
      editConfigIndex: -1,
      currentModalConfig: -1,
      errorObject: null,
      importErrorObject: null,
      isSupplementaryContract: false,
    };
  },
  watch: {
    editConfigIndex() {
      if (
        this.configurations[this.editConfigIndex] &&
        this.configurations[this.editConfigIndex].configurationTenants.length ==
          0
      ) {
        this.configurations[this.editConfigIndex].availableTenants.map(
          (aTenant) => {
            this.configurations[this.editConfigIndex].configurationTenants.push(
              {
                ConfigurationId: this.configurations[this.editConfigIndex].id,
                tenantId: aTenant.id,
                createdBy: this.$GetCurrentUserName(),
                modifiedBy: this.$GetCurrentUserName(),
              },
            );
          },
        );
      }
      if (
        this.configurations[this.editConfigIndex] &&
        this.configurations[this.editConfigIndex].wageSupplementTenants
          .length == 0
      ) {
        this.configurations[this.editConfigIndex].availableTenants.map(
          (aTenant) => {
            this.configurations[
              this.editConfigIndex
            ].wageSupplementTenants.push({
              ConfigurationId: this.configurations[this.editConfigIndex].id,
              tenantId: aTenant.id,
              createdBy: this.$GetCurrentUserName(),
              modifiedBy: this.$GetCurrentUserName(),
            });
          },
        );
      }
    },
  },
  created() {
    this.getAllConfigurations();
    this.checkIfSupplementaryContract();
  },
  methods: {
    // Helper method to get all tenants selected across all configurations
    getAllSelectedTenantIds() {
      const allSelectedTenants = new Set();

      this.configurations.forEach((config) => {
        // If configurationTenants is empty, treat as all tenants selected
        if (config.configurationTenants.length === 0) {
          config.allTenants.forEach((tenant) => {
            allSelectedTenants.add(tenant.id);
          });
        } else {
          config.configurationTenants.forEach((cTenant) => {
            allSelectedTenants.add(cTenant.tenantId);
          });
        }
      });

      return Array.from(allSelectedTenants);
    },

    // Helper method to get all tenants with wage supplement selected across all configurations
    getAllSelectedWageSupplementTenantIds() {
      const allSelectedTenants = new Set();

      this.configurations.forEach((config) => {
        // If wageSupplementTenants is empty, treat as all tenants selected
        if (config.wageSupplementTenants.length === 0) {
          config.allTenants.forEach((tenant) => {
            allSelectedTenants.add(tenant.id);
          });
        } else {
          config.wageSupplementTenants.forEach((cTenant) => {
            allSelectedTenants.add(cTenant.tenantId);
          });
        }
      });

      return Array.from(allSelectedTenants);
    },

    // Helper method to check if tenant is available (not selected in any other configuration)
    isTenantAvailableGlobally(tenantId, currentConfigIndex) {
      // First check if tenant is already selected in the current configuration
      // If yes, it should be available (so user can deselect it)
      const currentConfig = this.configurations[currentConfigIndex];

      // If current config has empty configurationTenants, treat as all selected
      if (currentConfig.configurationTenants.length === 0) {
        return true; // Always allow interaction when all tenants are selected
      }

      const isSelectedInCurrentConfig = currentConfig.configurationTenants.some(
        (cTenant) => cTenant.tenantId === tenantId,
      );

      if (isSelectedInCurrentConfig) {
        return true; // Always allow deselection
      }

      // Check if tenant is selected in any other configuration
      for (let i = 0; i < this.configurations.length; i++) {
        if (i !== currentConfigIndex) {
          const config = this.configurations[i];

          // If other config has empty configurationTenants, treat as all selected
          if (config.configurationTenants.length === 0) {
            return false; // Tenant is considered selected in this configuration
          }

          const isSelectedInOtherConfig = config.configurationTenants.some(
            (cTenant) => cTenant.tenantId === tenantId,
          );
          if (isSelectedInOtherConfig) {
            return false; // Tenant is already selected in another configuration
          }
        }
      }

      return true; // Tenant is available for selection
    },

    // Helper method to check if wage supplement tenant is available globally
    isWageSupplementTenantAvailableGlobally(tenantId, currentConfigIndex) {
      // First check if tenant is already selected for wage supplement in the current configuration
      const currentConfig = this.configurations[currentConfigIndex];

      // If current config has empty wageSupplementTenants, treat as all selected
      if (currentConfig.wageSupplementTenants.length === 0) {
        return true; // Always allow interaction when all tenants are selected
      }

      const isSelectedInCurrentConfig =
        currentConfig.wageSupplementTenants.some(
          (cTenant) => cTenant.tenantId === tenantId,
        );

      if (isSelectedInCurrentConfig) {
        return true; // Always allow deselection
      }

      // Check if tenant is selected for wage supplement in any other configuration
      for (let i = 0; i < this.configurations.length; i++) {
        if (i !== currentConfigIndex) {
          const config = this.configurations[i];

          // If other config has empty wageSupplementTenants, treat as all selected
          if (config.wageSupplementTenants.length === 0) {
            return false; // Tenant is considered selected in this configuration
          }

          const isSelectedInOtherConfig = config.wageSupplementTenants.some(
            (cTenant) => cTenant.tenantId === tenantId,
          );
          if (isSelectedInOtherConfig) {
            return false; // Tenant is already selected in another configuration
          }
        }
      }

      return true; // Tenant is available for selection
    },

    // Helper methods for checkbox logic
    isTenantConfigured(config, tenantId) {
      // If configurationTenants is empty, treat as all tenants selected
      if (config.configurationTenants.length === 0) {
        return true; // All tenants are considered configured
      }

      // Check if tenant is specifically configured
      return config.configurationTenants.some(
        (cTenant) => cTenant.tenantId === tenantId,
      );
    },

    isWageSupplementConfigured(config, tenantId) {
      // If wageSupplementTenants is empty, treat as all tenants selected
      if (config.wageSupplementTenants.length === 0) {
        return true; // All tenants are considered configured for wage supplement
      }

      // Check if tenant is specifically configured for wage supplement
      return config.wageSupplementTenants.some(
        (cTenant) => cTenant.tenantId === tenantId,
      );
    },

    isTenantCheckboxDisabled(config, configIndex, tenant) {
      // If not in edit mode for this config, disable checkbox
      if (this.editConfigIndex !== configIndex) {
        return true;
      }

      // Check if tenant is available globally (either already selected in current config or not selected anywhere else)
      const isTenantAvailableGlobally = this.isTenantAvailableGlobally(
        tenant.id,
        configIndex,
      );

      // If tenant is not available globally, disable it
      if (!isTenantAvailableGlobally) {
        return true;
      }

      // Check user role restrictions
      const hasAccess =
        this.$IsUserInRole("VertragsmanagementVerwaltung") ||
        (this.$IsUserInRole("VertragsmanagementUniaVertragsverantworlich") &&
          tenant.tenantId !== 4);

      return !hasAccess;
    },

    isWageSupplementCheckboxDisabled(config, configIndex, tenant) {
      // If not in edit mode for this config, disable checkbox
      if (this.editConfigIndex !== configIndex) {
        return true;
      }

      // Check if wage supplement tenant is available globally
      const isTenantAvailableGlobally =
        this.isWageSupplementTenantAvailableGlobally(tenant.id, configIndex);

      // If tenant is not available globally, disable it
      if (!isTenantAvailableGlobally) {
        return true;
      }

      // Check user role restrictions
      const hasRestrictedAccess =
        this.$IsUserInRole("VertragsmanagementUniaVertragsverantworlich") &&
        !this.$IsUserInRole("VertragsmanagementVerwaltung") &&
        tenant.tenantId !== 4;

      return hasRestrictedAccess;
    },

    hideModal() {
      this.$refs["deleteConfiguration"].hide();
      this.$refs["importConfiguration"].hide();
    },
    updatedCheckbox(newValue, tenantId) {
      if (newValue) {
        this.configurations[this.editConfigIndex].configurationTenants.push({
          ConfigurationId: this.configurations[this.editConfigIndex].id,
          tenantId: tenantId,
          createdBy: this.$GetCurrentUserName(),
          modifiedBy: this.$GetCurrentUserName(),
        });
      } else {
        var tenantConfig = this.configurations[
          this.editConfigIndex
        ].configurationTenants.filter((tConfig) => {
          return tConfig.tenantId == tenantId;
        });

        if (tenantConfig && tenantConfig.length == 1) {
          var index = this.configurations[
            this.editConfigIndex
          ].configurationTenants.indexOf(tenantConfig[0]);

          this.configurations[this.editConfigIndex].configurationTenants.splice(
            index,
            1,
          );
        }
      }
    },
    updatedSupplementCheckbox(newValue, tenantId) {
      if (newValue) {
        this.configurations[this.editConfigIndex].wageSupplementTenants.push({
          ConfigurationId: this.configurations[this.editConfigIndex].id,
          tenantId: tenantId,
          createdBy: this.$GetCurrentUserName(),
          modifiedBy: this.$GetCurrentUserName(),
        });
      } else {
        var tenantConfig = this.configurations[
          this.editConfigIndex
        ].wageSupplementTenants.filter((tConfig) => {
          return tConfig.tenantId == tenantId;
        });

        if (tenantConfig && tenantConfig.length == 1) {
          var index = this.configurations[
            this.editConfigIndex
          ].wageSupplementTenants.indexOf(tenantConfig[0]);

          this.configurations[
            this.editConfigIndex
          ].wageSupplementTenants.splice(index, 1);
        }
      }
    },
    getAllConfigurations() {
      this.isLoading = true;
      this.configurations = [];

      GetConfigurations(
        this.contractId,
        this.versionId,
        this.editionId,
        (result) => {
          // if there is no configuration yet, we have to create the first one.
          if (result.length == 0) {
            this.addNewConfiguration();
          } else {
            this.configurations = result;
            this.isLoading = false;
          }
        },
        (error) => {
          console.log(error);
          this.isLoading = false;
        },
      );
    },
    saveConfiguration() {
      this.isLoading = true;
      this.errorObject = null;

      SaveConfiguration(
        this.contractId,
        this.versionId,
        this.editionId,
        this.configurations[this.editConfigIndex],
        () => {
          this.isLoading = false;
          this.getAllConfigurations();
          this.editConfigIndex = -1;
        },
        (error) => {
          this.isLoading = false;
          this.errorObject = error;
          this.getAllConfigurations();
        },
      );
    },
    deleteConfiguration() {
      this.isLoading = true;
      this.errorObject = null;

      DeleteConfiguration(
        this.contractId,
        this.versionId,
        this.editionId,
        this.currentModalConfig,
        () => {
          this.getAllConfigurations();
        },
        (error) => {
          this.isLoading = false;
          this.errorObject = error;
          this.getAllConfigurations();
        },
      );
    },
    checkIfSupplementaryContract() {
      GetMainContracts(
        this.contractId,
        (result) => {
          this.isSupplementaryContract = result.length > 0;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    copyConfiguration(configurationId) {
      this.isLoading = true;
      this.errorObject = null;

      CopyConfiguration(
        this.contractId,
        this.versionId,
        this.editionId,
        configurationId,
        () => {
          this.getAllConfigurations();
        },
        (error) => {
          this.isLoading = false;
          this.errorObject = error;
          this.getAllConfigurations();
        },
      );
    },
    importResourcecalenderFile() {
      this.isLoadingImport = true;
      const file = this.$refs.importMiloSalaryFile.files[0];

      //Get the JSON from the file
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const jsonObject = JSON.parse(reader.result);
          if (jsonObject) {
            ImportConfigurations(
              this.contractId,
              this.versionId,
              this.editionId,
              this.importOverride,
              jsonObject,
              (respond) => {
                if (respond) {
                  this.getAllConfigurations();
                }
                this.importErrorObject = null;
                this.isLoadingImport = false;
              },
              (error) => {
                this.importErrorObject = error;
                this.isLoadingImport = false;
              },
            );
          }
        } catch (error) {
          this.importErrorObject = error;
          this.isLoadingImport = false;
        }
      };

      reader.onerror = (error) => {
        this.errorObject = error;
        this.isLoadingImport = false;
      };

                reader.readAsText(file);
            },
            getConfigurationExport() {
                this.isLoadingExport = true;

                ExportConfigurations(
                    this.contractId,
                    this.versionId,
                    this.editionId,
                    respond => {
                        if (respond) {
                            const url = window.URL.createObjectURL(respond);
                            const a = document.createElement("a");
                            a.download = `${this.contractId}_${this.versionId}_${this.editionId}.json`;
                            a.href = url;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            setTimeout(() => window.URL.revokeObjectURL(url), 100);
                        }
                        this.isLoadingExport = false;
                    },
                    error => {
                        this.errorObject = error;
                        this.isLoadingExport = false;
                    }
                );
            },
            addNewConfiguration() {
                this.isLoading = true;
                this.errorObject = null;

      var emptyConfiguration = {
        id: 0,
        editionId: this.editionId,
        configurationTenants: [],
      };

      AddNewConfiguration(
        this.contractId,
        this.versionId,
        this.editionId,
        emptyConfiguration,
        () => {
          this.getAllConfigurations();
        },
        (error) => {
          this.isLoading = false;
          this.errorObject = error;
        },
      );
    },
  },
};
</script>

<style lang="scss" scoped></style>
